import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { CommitmentProduct, DAY_COUNT_CONVENTIONS } from './types';
import { loanKeyByConvention } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/dataMappings/setFormValues';
import { useProductDiffResult } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/hooks';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import {
  SelectInput,
  ShowWhileEditing,
  TextInput,
  WholePageDiffOutline,
} from '~/core/routes/settings/implementations/common/DiffInputs';

const useAvailablePricingOptions = ({
  commitmentKey,
  currentlyEditingLoanKey,
}: {
  commitmentKey: string;
  currentlyEditingLoanKey: string | null;
}) => {
  const creditAgreement = useCreditAgreementStore(
    (store) => store.creditAgreement,
  );

  return useMemo(() => {
    const pricingOptions =
      creditAgreement?.loanMechanicsV1?.pricingOptions
        .map((p) => ({ label: p.name, value: p.key }))
        .sort() ?? [];

    const commitmentLoans =
      creditAgreement?.loanMechanicsV1?.commitments
        .find((c) => c.key === commitmentKey)
        ?.loans.map(({ loanKey }) =>
          creditAgreement?.loanMechanicsV1?.loans.find(
            (l) => l.key === loanKey,
          ),
        )
        .filter((l) => !!l) ?? [];

    const commitmentOtherLoans = currentlyEditingLoanKey
      ? commitmentLoans.filter((l) => l.key !== currentlyEditingLoanKey)
      : commitmentLoans;

    const pricingOptionsUsedByOther = commitmentOtherLoans.map(
      (l) => l.pricingOptionKey,
    );

    return pricingOptions.filter(
      (p) => !pricingOptionsUsedByOther.includes(p.value),
    );
  }, [
    commitmentKey,
    currentlyEditingLoanKey,
    creditAgreement?.loanMechanicsV1,
  ]);
};

type ProductsDrawerForm = {
  wipForm: CommitmentProduct;
  diffForm: CommitmentProduct | undefined;
};

type AddProductDrawerProps = {
  disclosure: UseDisclosureReturn;
  commitmentKey: string;
  onAddProduct: (product: CommitmentProduct) => void;
  existingProduct: CommitmentProduct | undefined;
};

export const AddProductDrawer = ({
  disclosure,
  commitmentKey,
  onAddProduct,
  existingProduct,
}: AddProductDrawerProps) => {
  // This drawer can be used to edit an existing product, or create a new one.
  const isEditingProduct = Boolean(existingProduct);

  const diffResult = useProductDiffResult(existingProduct?.pricingOptionKey);

  const form = useForm<ProductsDrawerForm>({
    mode: 'onTouched',
  });

  useEffect(() => {
    const diffForm = form.getValues('diffForm');
    form.reset({
      wipForm: existingProduct || { productType: 'loan' },
      diffForm,
    });
  }, [form, existingProduct]);

  useEffect(() => {
    form.setValue('diffForm', diffResult.previousProduct);
  }, [form, diffResult.previousProduct]);

  const pricingOptions = useAvailablePricingOptions({
    commitmentKey,
    currentlyEditingLoanKey: existingProduct
      ? loanKeyByConvention(existingProduct)
      : null,
  });

  const { isOpen, onClose } = disclosure;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit((data) => {
              onAddProduct(data.wipForm);
              onClose();
            })}
          >
            <DrawerHeader>
              {isEditingProduct ? 'Edit Product' : 'Add Product'}
            </DrawerHeader>
            <DrawerCloseButton />
            <WholePageDiffOutline diff={diffResult?.diffResult}>
              <DrawerBody display="flex" flexDirection="column" gap="1rem">
                <TextInput<ProductsDrawerForm>
                  name="wipForm.name"
                  diffName="diffForm.name"
                  label="Name"
                  placeholder="Enter Product Name"
                  required
                />
                <SelectInput<ProductsDrawerForm>
                  name="wipForm.productType"
                  diffName="diffForm.productType"
                  label="Product Type"
                  placeholder="Select"
                  required
                  options={[{ label: 'Loan', value: 'loan' }]}
                />
                <SelectInput<ProductsDrawerForm>
                  name="wipForm.pricingOptionKey"
                  diffName="diffForm.pricingOptionKey"
                  label="Pricing Option"
                  placeholder="Select"
                  required
                  helperText="Each pricing option can only be used once. Once selected, it will be disabled in the list. Configure pricing options in 'Pricing Options'"
                  options={pricingOptions}
                />
                {/* TODO: these are properties of the pricing option. Figure out what to do with this. */}
                {/* <InputContainer */}
                {/*  label="Margin" */}
                {/*  helperText="Applies any additional adjustments to the base rate before it takes effect." */}
                {/* > */}
                {/*  <SelectInput<ProductsDrawerForm> */}
                {/*    name="wipForm.marginType" */}
                {/*    label="Type" */}
                {/*    placeholder="Select" */}
                {/*    required */}
                {/*    options={[{ label: 'Fixed', value: 'fixed' }]} */}
                {/*  /> */}
                {/*  <TextInput<ProductsDrawerForm> */}
                {/*    name="wipForm.marginValue" */}
                {/*    label="Value" */}
                {/*    placeholder="0%" */}
                {/*    mask="percentage" */}
                {/*    required */}
                {/*  /> */}
                {/* </InputContainer> */}
                <SelectInput<ProductsDrawerForm>
                  name="wipForm.accrualBasis"
                  diffName="diffForm.accrualBasis"
                  label="Accrual Basis"
                  placeholder="Select"
                  required
                  options={DAY_COUNT_CONVENTIONS.map((x) => ({
                    label: x,
                    value: x,
                  }))}
                />
                <TextInput<ProductsDrawerForm>
                  name="wipForm.maturityMonths"
                  diffName="diffForm.maturityMonths"
                  label="Maturity Count in Months"
                  mask="positiveInteger"
                  required
                  helperText='When a loan is created, the selected maturity period (e.g. "1 month") will set a default Maturity Date based on the Funding Date. The loan maturity date can be edited as needed during the loan creation.'
                />
              </DrawerBody>
            </WholePageDiffOutline>

            <DrawerFooter>
              <Button
                variant="secondary"
                colorScheme="v2-aurora"
                mr={3}
                onClick={onClose}
              >
                Cancel
              </Button>
              <ShowWhileEditing>
                <Button colorScheme="v2-aurora" type="submit">
                  {isEditingProduct ? 'Save Changes' : 'Add Product'}
                </Button>
              </ShowWhileEditing>
            </DrawerFooter>
          </form>
        </FormProvider>
      </DrawerContent>
    </Drawer>
  );
};
